import React from "react";
import { Colors } from "@utils";
import { useUserInfo } from "@store/hooks";
import AuthStepView, {
  AuthStepDescription,
} from "@components/organisms/AuthStepView";
import { Button, Row } from "@components/atoms";
import warnIcon from "@assets/images/icon_warn.png";
import styled from "styled-components";
import {
  LoadCompletedDialog,
  LoadingDialog,
  SimpleDialog,
} from "@components/modals";
import { useAppDispatch, useAppSelector } from "@store";
import { afterAuth, startAuth } from "@store/slices/dataSlice";
import { useNavigate } from "react-router-dom";

export interface AuthTemplateProps {
  icon: string;
  title: string;
  authSteps: AuthStepDescription[];
  pathStr: string;
  helpStrs?: string[];
}

const HelpList = styled.ul`
  list-style: disc;
  list-style-position: inside;
  padding-left: 8px;
  color: #9399a4;
  font-size: 13px;
`;

const AuthTemplate = function (props: AuthTemplateProps) {
  const { icon, title, authSteps, pathStr, helpStrs } = props;

  const userInfo = useUserInfo();

  const authRequested = useAppSelector((state) => state.data.authRequested);

  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const [showLoader, setShowLoader] = React.useState<boolean>(false);
  const [showCompletedDialog, setShowCompletedDialog] =
    React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string>("");
  const [showErrorDialog, setShowErrorDialog] = React.useState<boolean>(false);

  const [authStarted, setAuthStarted] = React.useState<boolean>(false);

  const loading = React.useRef<boolean>(false);

  React.useEffect(() => {
    if (authRequested) return;

    dispatch(
      startAuth({
        authType: userInfo.authType!,
        userInfo: {
          name: userInfo.name,
          phone: userInfo.phone,
          birthday: userInfo.birthday,
        },
        onSuccess: () => {
          loading.current = false;
          setAuthStarted(true);
        },
        onError: (err) => {
          loading.current = false;
          setAuthStarted(false);
          setErrorMessage(err.message);
          setShowErrorDialog(true);
        },
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPressAuth = () => {
    if (loading.current) return;

    loading.current = true;
    setShowLoader(true);

    dispatch(
      afterAuth({
        authType: userInfo.authType!,
        userInfo: {
          name: userInfo.name,
          phone: userInfo.phone,
          birthday: userInfo.birthday,
        },
        onSuccess: () => {
          loading.current = false;
          setShowLoader(false);
          setShowCompletedDialog(true);
        },
        onError: (err) => {
          loading.current = false;
          setShowLoader(false);
          setErrorMessage(err.message);
          setShowErrorDialog(true);
        },
      })
    );
  };

  const gotoNextPage = () => {
    setShowCompletedDialog(false);
    navigate("/result");
  };

  return (
    <div className="flex flex-col px-[22px] pt-[40px] pb-[22px] min-h-full">
      <div className="flex flex-col px-[8px]">
        <img src={icon} className="w-[48px] h-[48px] mb-[25px]" alt="" />
        <div
          className="whitespace-pre-wrap text-[20px] font-[600] leading-[30px]"
          style={{
            color: Colors.totallyBlack,
          }}
        >
          {title}
        </div>
        <div
          className="text-[14px] font-[500] mt-[8px] mb-[30px]"
          style={{ color: "#9399A4" }}
        >
          {userInfo.name}님의 휴대폰으로 인증 요청을 보냈습니다.
        </div>
        <AuthStepView authSteps={authSteps} />
        <div className="mt-[44px]">
          <Row className="mb-[12px]">
            <img
              src={warnIcon}
              className="w-[24px] h-[24px] mr-[10px]"
              alt=""
            />
            <div
              className="text-[16px] font-[600]"
              style={{ color: Colors.black }}
            >
              인증요청 알림이 오지 않나요?
            </div>
          </Row>
          <HelpList>
            <li>
              <b>{pathStr}</b>에서 인증 요청 내용을 확인할 수 있어요.
            </li>
            {helpStrs && helpStrs.length > 0
              ? helpStrs.map((helpStr, i) => (
                  <li
                    key={`help_str_${i}`}
                    style={{
                      marginTop: 15,
                    }}
                  >
                    {helpStr}
                  </li>
                ))
              : null}
          </HelpList>
        </div>
      </div>
      <div className="grow" />
      <Button
        disabled={!authStarted}
        style={{ marginTop: 20 }}
        bgColor={Colors.primaryGreen}
        color={Colors.white}
        onClick={onPressAuth}
      >
        인증 완료
      </Button>
      <LoadingDialog text="내역을 불러오는중 입니다." isOpen={showLoader} />
      <LoadCompletedDialog onOK={gotoNextPage} isOpen={showCompletedDialog} />
      <SimpleDialog
        message={errorMessage}
        onOK={() => {
          setShowErrorDialog(false);
          if (!authStarted) {
            navigate(-2);
          }
        }}
        isOpen={showErrorDialog}
      />
    </div>
  );
};

export default AuthTemplate;
